// Вход по номеру телефона, по паролю и сессия. Экран 01.
//
// ⚠️ Здесь стояло «завести аккаунт может только номер, подтверждённый кодом».
// Утверждение перевёрнуто 09.09.2026 (SMY-150): аккаунт заводит ПЕРВЫЙ ЗАПУСК
// приложения, а код — из письма, а не из SMS — только подтверждает уже
// существующий. Разбор — в шапке `apps/api/src/auth/service.ts`.
//
// Ручки ниже переезжают на почту в Э4 (SMY-164, SMY-166, SMY-167), и до тех
// пор описывают контур первой волны — вход по номеру. Оставлено намеренно:
// переписать здесь раньше сервера значило бы получить клиент, зовущий ручки,
// которых нет.

import type { TariffId } from '@smyslika/core'

import type { Client } from './client.ts'

export interface CodeRequested {
  sent: true
  /** Через сколько секунд можно просить код заново — кнопке есть что показать. */
  resendAfterSeconds: number
}

export interface Session {
  token: string
  expiresAt: string
}

export interface Me {
  userId: string
  tariff: TariffId
  /**
   * Свой номер, E.164. Пусто у всех, кто пришёл после перехода на почту:
   * аккаунт заводит первый запуск, номера у него нет и не будет (SMY-152).
   * Уйдёт совсем вместе с входом по номеру (SMY-182).
   */
  phone?: string
  /** Имени может не быть: третий шаг регистрации можно пропустить. */
  name?: string
  /**
   * Подтверждённая почта — логин и способ восстановить доступ.
   *
   * ⚠️ ТОЛЬКО ПОДТВЕРЖДЁННАЯ (11.09.2026). Здесь стояло «почта для чека»: с
   * тех пор та же колонка стала логином, а у первой волны в строке бывает
   * адрес, вписанный без кода, — войти по нему нельзя. Такого сервер не
   * присылает: поля нет у гостя и у первой волны до привязки.
   */
  email?: string
  /** Фотография. Ссылка подписанная и живёт минуты — сохранять её нельзя. */
  avatarUrl?: string
  /**
   * Задан ли пароль. Не сам пароль и не его хеш — только факт.
   *
   * От него зависит и подпись в профиле, и то, предлагать ли завести пароль
   * после первого готового ответа.
   */
  hasPassword: boolean
  /**
   * Лестница регистрации у гостя. SMY-161
   *
   * ⚠️ У ЗАРЕГИСТРИРОВАННОГО ПОЛЯ НЕТ ВОВСЕ — не `blocked: false`. Отсюда и
   * правило для клиента: показывать по НАЛИЧИЮ поля, а не по его
   * содержимому. Проверка «а если зарегистрирован, то не показывать» на
   * клиенте — это логика, которую поправят на одной платформе из двух.
   *
   * Считает сервер целиком: клиент не помнит отказы и не изобретает пороги.
   * Отказ отправляется в `declineSignup`, а не запоминается на устройстве, —
   * лестница управляет СТЕНОЙ, а стена, которую снимает переустановка, не
   * стена.
   */
  signup?: {
    doneChats: number
    /**
     * Пусто — просить больше не будем, дальше только стена. Ступень стены сюда
     * не попадает никогда: она не просьба.
     */
    nextAskAt?: number
    /** Эта просьба — последняя перед стеной. Меняет ТЕКСТ, а не поведение. */
    lastAsk: boolean
    /** На каком чате встаёт стена. Нужен экрану стены, чтобы назвать число. */
    wallAt: number
    blocked: boolean
  }
  /**
   * Предложение завести пароль. SMY-168
   *
   * ⚠️ ЗАМЕНИЛО `hasResult`. Тот был признаком, по которому клиент решал сам:
   * «после первого готового ответа». Довод остался верным — пароль защищает
   * нажитое, — но решает теперь сервер и по своей лестнице (+3 успешных чата
   * после регистрации, потом ещё +3, дальше никогда), а отказ помнит он же.
   *
   * Поля нет вовсе, когда предлагать нечего: пароль уже есть, ступени
   * кончились или человек ещё не зарегистрирован.
   */
  /**
   * Аккаунту нужно привязать почту. SMY-181
   *
   * ⚠️ ВРЕМЕННОЕ ПОЛЕ, снимается вместе с входом по номеру (SMY-182).
   * Существует для первой волны: люди пришли по номеру, номер уходит, и между
   * «почта работает» и «номер снят» им нужен путь — иначе они однажды просто
   * не смогут войти.
   *
   * У гостя `false`: ему предлагают не привязку, а регистрацию.
   *
   * Считается по ПОДТВЕРЖДЕНИЮ, а не по наличию адреса: адрес, вписанный без
   * кода, не привязка — войти по нему нельзя.
   */
  needsEmail: boolean
  passwordOffer?: {
    /** Успешных чатов ПОСЛЕ регистрации, а не с начала жизни аккаунта. */
    doneChats: number
    nextAskAt?: number
  }
}

export interface Auth {
  requestCode(phone: string): Promise<CodeRequested>
  /** Успех сам кладёт токен в хранилище: забыть это — классическая ошибка. */
  verify(phone: string, code: string, device?: string): Promise<Session>
  /**
   * Вход по паролю. Логин — ПОЧТА. Аккаунта не заводит: незнакомому адресу
   * сервер отвечает тем же отказом, что и при неверном пароле.
   */
  withPassword(email: string, password: string, device?: string): Promise<Session>
  me(): Promise<Me>
  logout(): Promise<void>
  /**
   * «Не сейчас» на предложении зарегистрироваться. SMY-161
   *
   * Отправляется на сервер, а не запоминается на устройстве: следующая
   * просьба должна прийти на ступень позже, а не на следующем же чате, — и
   * пережить переустановку, потому что лестница управляет стеной.
   */
  /**
   * Завести гостя и получить сессию. SMY-159
   *
   * ⚠️ ЗВАТЬ ТОЛЬКО ПРИ ОТСУТСТВИИ ТОКЕНА. Ручка не идемпотентна: второй
   * вызов заводит второго гостя, а первый вместе со всей его историей
   * остаётся сиротой на устройстве, которое о нём забыло. Проверить это на
   * сервере нечем — два запроса от одного устройства он не отличает.
   *
   * Успех сам кладёт токен в хранилище, как и `verify`.
   */
  startGuest(device?: string): Promise<Session>
  /** Код на почту. Отвечает одинаково и для свободного адреса, и для занятого. */
  requestEmailCode(email: string): Promise<CodeRequested>
  /**
   * Подтверждение почты. SMY-164
   *
   * ⚠️ ТРИ ИСХОДА, и клиент обязан различать их все:
   *
   * * `confirmed` — гость подтвердил почту в СВОЁМ аккаунте. Токена в ответе
   *   нет: сессия осталась прежней, и класть в хранилище нечего;
   * * `created` — заведён новый аккаунт (веб, второе устройство);
   * * `signed_in` — адрес занят, вошли в существующий. `note` объясняет, что
   *   гостевая история туда НЕ переехала, и показать её обязательно: иначе
   *   человек решит, что потерял свои чаты по нашей вине.
   *
   * Токен кладётся в хранилище там, где он есть.
   */
  verifyEmail(
    email: string,
    code: string,
    device?: string,
  ): Promise<{ outcome: 'confirmed' | 'created' | 'signed_in'; userId: string; note?: string }>
  declineSignup(): Promise<void>
  /** «Не сейчас» на предложении завести пароль. Свой счётчик. SMY-168 */
  declinePassword(): Promise<void>
}

export function createAuth(client: Client): Auth {
  return {
    async requestCode(phone) {
      return client.request<CodeRequested>('/v1/auth/request-code', {
        body: { phone },
        anonymous: true,
      })
    },

    async verify(phone, code, device) {
      const session = await client.request<Session>('/v1/auth/verify', {
        body: { phone, code, ...(device != null ? { device } : {}) },
        anonymous: true,
      })
      client.tokens.set(session.token)
      return session
    },

    async withPassword(email, password, device) {
      const session = await client.request<Session>('/v1/auth/password', {
        body: { email, password, ...(device != null ? { device } : {}) },
        anonymous: true,
      })
      client.tokens.set(session.token)
      return session
    },

    async startGuest(device) {
      const session = await client.request<Session>('/v1/auth/guest', {
        body: device != null ? { device } : {},
        anonymous: true,
      })
      client.tokens.set(session.token)
      return session
    },

    async requestEmailCode(email) {
      return client.request<CodeRequested>('/v1/auth/email/request-code', {
        body: { email },
        anonymous: true,
      })
    },

    async verifyEmail(email, code, device) {
      // НЕ anonymous: гостевой токен — это и есть развилка между «подтвердил
      // свой аккаунт» и «завёл новый». Отправив запрос без него, приложение
      // молча завело бы человеку второй аккаунт и осиротило первый.
      const res = await client.request<{
        outcome: 'confirmed' | 'created' | 'signed_in'
        userId: string
        note?: string
        token?: string
      }>('/v1/auth/email/verify', { body: { email, code, ...(device != null ? { device } : {}) } })
      if (res.token != null) client.tokens.set(res.token)
      return res
    },

    async declineSignup() {
      await client.request('/v1/auth/signup-declined', { method: 'POST' })
    },

    async declinePassword() {
      await client.request('/v1/auth/password-declined', { method: 'POST' })
    },

    async me() {
      return client.request<Me>('/v1/me')
    },

    async logout() {
      // Чистим токен даже если запрос не дошёл: пользователь нажал «выйти»,
      // и остаться внутри — худшее, что может случиться.
      try {
        await client.request('/v1/auth/logout', { method: 'POST' })
      } finally {
        client.tokens.set(null)
      }
    },
  }
}
